import React, { useState, useEffect } from 'react';
import { sortBy } from 'lodash';
import { useSelector } from 'react-redux';
import { useHistory } from 'react-router-dom';
import { FormattedMessage } from 'react-intl';
import makeStyles from '@material-ui/core/styles/makeStyles';
import Typography from '@material-ui/core/Typography';
import Tooltip from '@material-ui/core/Tooltip';
import Paper from '@material-ui/core/Paper';
import Button from '@material-ui/core/Button';
import useQueryParams from '../utils/useQueryParams';
import ProgressFullscreen from '../components/common/ProgressFullscreen';
import { MARKET_GLOBAL_LANG } from '../features/app/constants';
import { ppsiCreateFromExistingRoute, ppsiCreateRoute } from '../routes';
import { States } from '../features/PpiContainer/constants';
import ArrowBackIcon from '@material-ui/icons/ArrowBack';
import View from './views';
import { toJS } from 'utils/immutableUtils';
import { getLanguage } from 'features/app/selectors';
import usePpsis from 'features/PpsiContainer/hooks/usePpsis';
import PpsiList from 'components/PpsiList/PpsiList';
import PpsiListSearch from 'components/PpsiList/PpsiListSearch';
import usePreviewPpsi from 'features/PpsiContainer/hooks/usePreviewPpsi';
import useCopyLocalPpsis from 'features/PpsiContainer/hooks/useCopyLocalPpsis';

const useStyles = makeStyles(theme => ({
  container: {
    padding: theme.spacing(4)
  },
  paper: {
    margin: '0 auto',
    padding: theme.spacing(3),
    width: '75%'
  },
  actionsContainer: {
    marginTop: theme.spacing(2)
  },
  copyButton: {
    color: '#fff',
    marginRight: theme.spacing(2)
  },
  createButton: {
    padding: '6px 16px'
  },
  goBackButton: {
    padding: '6px 16px',
    marginLeft: '345px',
    float: 'right'
  }
}));

/**
 * View where user selects which PPSI to copy data from when creating a PPSI.
 *
 * Note: Only works when providing id of global PPSI (as query param fromPpsiId)
 */
const PpsiCreateFromPublishedLocalPpsiView = () => {
  const classes = useStyles();
  const [formulaCode, localPpsiCopy] = useQueryParams(
    'formulaCode',
    'localPpsiCopy'
  );
  // const [isFetching, ppsi] = usePpsis({ status: 'COPYLOCAL' });
  const [isFetching, ppsi] = useCopyLocalPpsis();
  const ppsis = toJS(ppsi, []);
  const [fromPpsiId, setFromPpsiId] = useState(null);
  const [fromPpsiDataId, setFromPpsiDataId] = useState(null);
  const history = useHistory();
  const [previewNode, openPreview] = usePreviewPpsi();
  const [searchInput, setSearchInput] = useState('');
  const [localData, setLocalData] = useState([]);
  const languages = useSelector(getLanguage);

  function filterData() {
    const data = ppsis.filter(i => {
      // if (i.publishedPpsiEnrichmentData) {
      const iFoodId = i.formulaCode
        .toLowerCase()
        .includes(searchInput.toLowerCase().replace(/\s+/g, ''));
      const piaNumber = i.piaArticleNumber.includes(searchInput);

      const name = i.info[languages].presentationName;
      const finalName = name.trim().toLowerCase();
      return finalName.includes(searchInput) || iFoodId || piaNumber;
      // }
    });
    setLocalData(data);
  }

  useEffect(() => {
    if (searchInput.length === 0) {
      setLocalData(isFetching ? [] : ppsis);
    } else {
      filterData();
    }
  }, [isFetching, searchInput]);

  if (isFetching) {
    return <ProgressFullscreen />;
  }

  const handlePpsiSelected = (event, { id }) => {
    setFromPpsiId(id);
    setFromPpsiDataId(event.target.value);
  };

  const handleClickCopyPpsi = () => {
    if (!fromPpsiId) {
      alert('Select instruction to copy');
      return;
    }
    history.replace(
      ppsiCreateFromExistingRoute(
        formulaCode,
        fromPpsiId,
        fromPpsiDataId,
        localPpsiCopy
      )
    );
  };

  View.PpsiCreateFromExistingSelectPpsi.visit();

  function navigateBack() {
    history.goBack();
  }

  return (
    <div className={classes.container}>
      {previewNode}
      <Paper className={classes.paper}>
        <div style={{ display: 'flex', justifyContent: 'space-between' }}>
          <Typography variant="h6">
            <FormattedMessage id="ppsi.createFromExistingSelectPpsiHeader" />
          </Typography>

          {localPpsiCopy === 'true' && (
            <div>
              <PpsiListSearch
                onChangeInput={setSearchInput}
                filterData={filterData}
                searchInput={searchInput}
              />
            </div>
          )}
        </div>
        <PpsiList
          ppsis={sortBy(localData, `info.${languages}.presentationName`)}
          selectedPpsiDataId={fromPpsiDataId}
          language={languages || MARKET_GLOBAL_LANG}
          onChangeSelectedPpsi={handlePpsiSelected}
          openPreview={openPreview}
          localPpsiCopy={localPpsiCopy}
        />
        <div className={classes.actionsContainer}>
          <Button
            variant="contained"
            color="secondary"
            onClick={handleClickCopyPpsi}
            classes={{ root: classes.copyButton }}
          >
            <FormattedMessage id="ppsi.copyInstructionButtonLabel" />
          </Button>
          <Tooltip title="Go back to previous page">
            <Button
              variant="text"
              startIcon={<ArrowBackIcon />}
              position="left"
              classes={{ root: classes.goBackButton }}
              onClick={navigateBack}
            >
              <FormattedMessage id="ppsi.goBack" />
            </Button>
          </Tooltip>
        </div>
      </Paper>
    </div>
  );
};

export default PpsiCreateFromPublishedLocalPpsiView;
